import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface HeroSlide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  video: string;
  poster: string;
  gradient: string;
  cta: string;
  ctaIcon: string;
  secondaryCta: string;
  secondaryIcon: string;
  videoType: 'mp4' | 'youtube';
  youtubeId?: string;
}

interface HeroContextType {
  heroSlides: HeroSlide[];
  updateHeroSlides: (slides: HeroSlide[]) => void;
  updateSlide: (slide: HeroSlide) => void;
  addSlide: (slide: HeroSlide) => void;
  deleteSlide: (id: number) => void;
}

const HeroContext = createContext<HeroContextType | undefined>(undefined);

export const useHeroContext = () => {
  const context = useContext(HeroContext);
  if (!context) {
    throw new Error('useHeroContext must be used within a HeroProvider');
  }
  return context;
};

interface HeroProviderProps {
  children: ReactNode;
}

export const HeroProvider: React.FC<HeroProviderProps> = ({ children }) => {
  // Load initial data from localStorage or use default
  const getInitialSlides = (): HeroSlide[] => {
    try {
      const saved = localStorage.getItem('heroSlides');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading hero slides from localStorage:', error);
    }
    
    // Default slides if no saved data
    return [
      {
        id: 1,
        title: "Good Music\nGreat Strings",
        subtitle: "Premium guitar strings for legendary performances",
        description: "Feel the power of every note with strings designed for rock, metal, and blues. Experience the raw energy and sustain that defines great music.",
        video: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4",
        poster: "https://images.unsplash.com/photo-1511379938547-c1f69419868d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=90",
        gradient: "from-red-600 via-orange-700 to-yellow-800",
        cta: "Rock Strings",
        ctaIcon: "🎸",
        secondaryCta: "Watch Demo",
        secondaryIcon: "🎥",
        videoType: "mp4"
      },
      {
        id: 2,
        title: "Jazz & Blues",
        subtitle: "Smooth tones for soulful performances",
        description: "Discover the warm, rich tones perfect for jazz and blues. Every string resonates with the soul of classic music.",
        video: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4",
        poster: "https://images.unsplash.com/photo-1550291652-6ea9114a47b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=90",
        gradient: "from-blue-600 via-indigo-700 to-purple-800",
        cta: "Jazz Strings",
        ctaIcon: "🎷",
        secondaryCta: "Listen Now",
        secondaryIcon: "🎵",
        videoType: "mp4"
      },
      {
        id: 3,
        title: "Acoustic Magic",
        subtitle: "Natural tones for acoustic excellence",
        description: "Experience the pure, unamplified sound that acoustic guitars are known for. Perfect for folk, country, and acoustic rock.",
        video: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
        poster: "https://images.unsplash.com/photo-1516924962500-2b4b3b99ea02?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=90",
        gradient: "from-green-600 via-emerald-700 to-teal-800",
        cta: "Acoustic Strings",
        ctaIcon: "🎼",
        secondaryCta: "Hear Samples",
        secondaryIcon: "🎧",
        videoType: "mp4"
      },
      {
        id: 4,
        title: "YouTube Music",
        subtitle: "Streaming music videos for inspiration",
        description: "Watch amazing guitar performances and music videos to inspire your next musical journey.",
        video: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
        poster: "",
        gradient: "from-purple-600 via-pink-700 to-red-800",
        cta: "Watch More",
        ctaIcon: "🎬",
        secondaryCta: "Subscribe",
        secondaryIcon: "📺",
        videoType: "youtube",
        youtubeId: "dQw4w9WgXcQ"
      }
    ];
  };

  const [heroSlides, setHeroSlides] = useState<HeroSlide[]>(getInitialSlides);

  const updateHeroSlides = (slides: HeroSlide[]) => {
    setHeroSlides(slides);
    localStorage.setItem('heroSlides', JSON.stringify(slides));
  };

  const updateSlide = (slide: HeroSlide) => {
    const updatedSlides = heroSlides.map(s => s.id === slide.id ? slide : s);
    setHeroSlides(updatedSlides);
    localStorage.setItem('heroSlides', JSON.stringify(updatedSlides));
  };

  const addSlide = (slide: HeroSlide) => {
    const updatedSlides = [...heroSlides, slide];
    setHeroSlides(updatedSlides);
    localStorage.setItem('heroSlides', JSON.stringify(updatedSlides));
  };

  const deleteSlide = (id: number) => {
    const updatedSlides = heroSlides.filter(s => s.id !== id);
    setHeroSlides(updatedSlides);
    localStorage.setItem('heroSlides', JSON.stringify(updatedSlides));
  };

  const value: HeroContextType = {
    heroSlides,
    updateHeroSlides,
    updateSlide,
    addSlide,
    deleteSlide
  };

  return (
    <HeroContext.Provider value={value}>
      {children}
    </HeroContext.Provider>
  );
};
